import React, { useState, useEffect } from "react";
import { useApp } from "../../contexts/AppContext";
import { useTheme } from "../../contexts/ThemeContext";
import { executionService } from "../../services/executionService";

export const ExecutionManagement = () => {
  const { service } = useApp();
  const { theme } = useTheme();
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, awaiting, completed
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [approvers, setApprovers] = useState([]);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [reminderStatus, setReminderStatus] = useState("");

  useEffect(() => {
    loadExecutions();
  }, [filter]);

  const loadExecutions = async () => {
    setLoading(true);
    try {
      let filterStr = "";
      if (filter === "awaiting") {
        filterStr = "fields/Status eq 'In Progress'";
      } else if (filter === "completed") {
        filterStr = "fields/Status eq 'Completed'";
      }

      const data = await executionService.getExecutions(service, filterStr);
      setExecutions(data);
      setSelectedExecution(null);
    } catch (error) {
      console.error("Error loading executions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectExecution = async (execution) => {
    setSelectedExecution(execution);
    const approverList = await executionService.getExecutionApprovers(service, execution.id);
    setApprovers(approverList);
  };

  const handleSendReminder = async (approver) => {
    if (!selectedExecution) return;

    setSendingReminder(true);
    setReminderStatus("");
    try {
      await executionService.sendApprovalReminder(service, {
        approverEmail: approver.ApproverEmail,
        approverName: approver.ApproverName,
        contractRef: selectedExecution.ContractRefNo,
        contractTitle: selectedExecution.ContractTitle,
        executionId: selectedExecution.id
      });
      setReminderStatus(`✓ Reminder sent to ${approver.ApproverName}`);
      setTimeout(() => setReminderStatus(""), 3000);
    } catch (error) {
      setReminderStatus(`✗ Failed to send reminder: ${error.message}`);
      setTimeout(() => setReminderStatus(""), 5000);
    } finally {
      setSendingReminder(false);
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "Pending":
        return "bg-yellow-900/30 text-yellow-400";
      case "Approved":
        return "bg-green-900/30 text-green-400";
      case "Rejected":
        return "bg-red-900/30 text-red-400";
      case "Skipped":
        return "bg-slate-600 text-slate-300";
      case "Waiting":
        return "bg-blue-900/30 text-blue-400";
      default:
        return "bg-slate-700 text-slate-300";
    }
  };

  const getExecutionStatusBadgeColor = (status) => {
    switch (status) {
      case "In Progress":
        return "bg-orange-900/30 text-orange-400";
      case "Completed":
        return "bg-green-900/30 text-green-400";
      case "Rejected":
        return "bg-red-900/30 text-red-400";
      default:
        return "bg-slate-700 text-slate-300";
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-serif text-3xl font-medium text-light-text dark:text-dark-text m-0 mb-1">
          Contract Executions
        </h1>
        <p className="text-light-text-secondary dark:text-dark-text-secondary m-0">Manage contract approvals and signatures</p>
        <div className="h-0.5 bg-gradient-to-r from-yellow-600 to-transparent mt-2" />
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6">
        {["all", "awaiting", "completed"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f
                ? "bg-yellow-600 text-slate-900"
                : "bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text dark:hover:text-dark-text"
            }`}
          >
            {f === "all" && "All Executions"}
            {f === "awaiting" && "Awaiting Execution"}
            {f === "completed" && "Completed"}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Executions List */}
        <div className="lg:col-span-2">
          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-xl overflow-hidden">
            {loading ? (
              <div className="p-12 text-center text-light-text-secondary dark:text-dark-text-secondary">Loading executions...</div>
            ) : executions.length === 0 ? (
              <div className="p-12 text-center text-light-text-secondary dark:text-dark-text-secondary">No executions found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-light-bg-tertiary dark:bg-dark-bg-tertiary border-b border-light-border dark:border-dark-border">
                      <th className="px-4 py-3 text-left text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary uppercase">Contract</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary uppercase">Progress</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary uppercase">Initiated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {executions.map((execution) => (
                      <tr
                        key={execution.id}
                        onClick={() => handleSelectExecution(execution)}
                        className="border-b border-light-border dark:border-dark-border hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-light-text dark:text-dark-text">{execution.ContractRefNo}</div>
                          <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary">{execution.ContractTitle}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getExecutionStatusBadgeColor(execution.Status)}`}>
                            {execution.Status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-light-text dark:text-dark-text">
                          {execution.ApproversCompleted}/{execution.TotalApprovers} signed
                        </td>
                        <td className="px-4 py-3 text-xs text-light-text-secondary dark:text-dark-text-secondary">
                          {new Date(execution.InitiatedAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Execution Details */}
        <div>
          {selectedExecution ? (
            <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-xl p-5">
              <h3 className="font-serif text-lg font-medium text-light-text dark:text-dark-text mb-4">{selectedExecution.ContractRefNo}</h3>

              <div className="space-y-4 text-sm">
                <div>
                  <label className="text-light-text-secondary dark:text-dark-text-secondary">Status</label>
                  <div className="mt-1">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getExecutionStatusBadgeColor(selectedExecution.Status)}`}>
                      {selectedExecution.Status}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="text-light-text-secondary dark:text-dark-text-secondary">Progress</label>
                  <div className="mt-2">
                    <div className="w-full bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-full h-2">
                      <div
                        className="bg-green-600 h-2 rounded-full transition-all"
                        style={{
                          width: `${(selectedExecution.ApproversCompleted / selectedExecution.TotalApprovers) * 100}%`
                        }}
                      />
                    </div>
                    <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-2">
                      {selectedExecution.ApproversCompleted} of {selectedExecution.TotalApprovers} approvers signed
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-light-text-secondary dark:text-dark-text-secondary block mb-2">Approvers</label>
                  <div className="space-y-2">
                    {approvers.map((approver, i) => (
                      <div key={i} className="p-2 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded text-xs">
                        <div className="flex items-center gap-2 justify-between">
                          <div className="flex items-center gap-2 flex-1">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusBadgeColor(approver.Status)}`}>
                              {approver.Status}
                            </span>
                            <span className="text-light-text dark:text-dark-text">{approver.ApproverName}</span>
                          </div>
                          {approver.Status === "Pending" && (
                            <button
                              onClick={() => handleSendReminder(approver)}
                              disabled={sendingReminder}
                              className="px-2 py-1 bg-yellow-600/30 hover:bg-yellow-600/50 text-yellow-400 rounded text-xs font-medium transition-colors disabled:opacity-50"
                              title="Send approval reminder"
                            >
                              {sendingReminder ? "..." : "Remind"}
                            </button>
                          )}
                        </div>
                        <div className="text-light-text-secondary dark:text-dark-text-secondary mt-1">{approver.ApproverEmail}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {reminderStatus && (
                  <div className={`mt-4 p-3 rounded text-xs ${
                    reminderStatus.includes("✓")
                      ? "bg-green-900/30 text-green-400"
                      : "bg-red-900/30 text-red-400"
                  }`}>
                    {reminderStatus}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-xl p-5 text-center text-light-text-secondary dark:text-dark-text-secondary">
              Select an execution to view details
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
